import { HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { Observable, map } from 'rxjs';

import { GeoPoint } from '@core/models/geo.state';
import {
  mapHeroStatsResponse,
  mapListingDetailResponse,
  mapListingSummaryResponse,
  mapSearchSuggestionResponse
} from '@core/models/listing.mapper';
import {
  HeroStatsResponse,
  ListingDetailResponse,
  ListingSummaryResponse,
  SearchSuggestionResponse
} from '@core/models/listing.response';
import { HeroStatsState, ListingDetailState, ListingSummaryState, SearchSuggestion } from '@core/models/listing.state';
import { PaginatedResult } from '@core/models/paginated.response';
import { ApiService } from '@core/services/api.service';

import {
  ListingRepository,
  ListingSearchOptions,
  ListingSearchResult,
  ReportListingRequest,
  SearchFacets
} from './listing.repository';

/** Appends only defined values; array values append one param per item. */
function toHttpParams(values: Record<string, string | number | (string | number)[] | undefined>): HttpParams {
  let params = new HttpParams();
  for (const [key, value] of Object.entries(values)) {
    if (value === undefined) continue;
    if (Array.isArray(value)) {
      for (const item of value) params = params.append(key, item);
    } else {
      params = params.set(key, value);
    }
  }
  return params;
}

@Injectable()
export class ListingHttpRepository implements ListingRepository {
  private readonly api = inject(ApiService);

  search(options: ListingSearchOptions): Observable<ListingSearchResult> {
    const params = toHttpParams({
      Q: options.q,
      CategorySlug: options.categorySlug,
      TenantId: options.tenantId,
      TenantSlug: options.tenantSlug,
      StoreIds: options.storeIds,
      Format: options.format,
      Conditions: options.conditions,
      Fulfilment: options.fulfilment,
      PriceMin: options.priceMin,
      PriceMax: options.priceMax,
      OriginLat: options.origin?.lat,
      OriginLng: options.origin?.lng,
      MaxDistanceMi: options.maxDistanceMi,
      EndingWithinHours: options.endingWithinHours,
      Sort: options.sort,
      PageNumber: options.pageNumber,
      PageSize: options.pageSize
    });
    return this.api
      .get<PaginatedResult<ListingSummaryResponse> & { facets: SearchFacets }>('listings/search', params)
      .pipe(map(page => ({ ...page, items: page.items.map(mapListingSummaryResponse), facets: page.facets })));
  }

  getBySlugId(slugId: string): Observable<ListingDetailState | null> {
    return this.api
      .get<ListingDetailResponse | null>(`listings/${slugId}`)
      .pipe(map(response => (response ? mapListingDetailResponse(response) : null)));
  }

  getByIds(ids: number[]): Observable<ListingSummaryState[]> {
    return this.api
      .post<ListingSummaryResponse[]>('listings/by-ids', { ids })
      .pipe(map(items => items.map(mapListingSummaryResponse)));
  }

  suggest(q: string): Observable<SearchSuggestion[]> {
    return this.api
      .get<SearchSuggestionResponse[]>('listings/suggest', new HttpParams().set('Q', q))
      .pipe(map(items => items.map(mapSearchSuggestionResponse)));
  }

  moreFromStore(storeId: number, excludeListingId: number, take: number): Observable<ListingSummaryState[]> {
    const params = new HttpParams().set('ExcludeListingId', excludeListingId).set('Take', take);
    return this.api
      .get<ListingSummaryResponse[]>(`stores/${storeId}/listings/more`, params)
      .pipe(map(items => items.map(mapListingSummaryResponse)));
  }

  getStats(origin: GeoPoint, radiusMi: number): Observable<HeroStatsState> {
    const params = new HttpParams().set('OriginLat', origin.lat).set('OriginLng', origin.lng).set('RadiusMi', radiusMi);
    return this.api.get<HeroStatsResponse>('listings/stats', params).pipe(map(mapHeroStatsResponse));
  }

  reportListing(request: ReportListingRequest): Observable<void> {
    return this.api.post<void>(`listings/${request.listingId}/reports`, {
      reason: request.reason,
      detail: request.detail,
      shopperId: request.shopperId
    });
  }
}
